import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { environment } from "environments/environment";
import { ReturnRequestDetail } from "../model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "../shared/dialog-base.component";
import { DomainService } from "../shared/domain.service";
import { UtilFns } from "../shared/util-fns";

export interface ReceiveRrdDialogParams extends DialogParams {
  rrdId: number;
}

/**
 * Pass 1 dialog for the RECEIVING phase. Operator enters the qty actually
 * received and confirms; on success the server records the qty, transitions
 * the RRD to INSPECTING, and emits a ReturnReceived JoNote. The dialog
 * resolves to true on success so the caller can refresh the row in place.
 */
@Component({
  selector: "app-receive-rrd-dialog",
  templateUrl: "./receive-rrd-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ReceiveRrdDialogComponent extends DialogBaseComponent<ReceiveRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;
  receivedQty: number | null = null;
  submitting = false;
  errorMessage: string | null = null;

  @ViewChild("qtyInput") qtyInput?: ElementRef<HTMLInputElement>;

  constructor(
    config: DynamicDialogConfig<ReceiveRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd) {
      this.receivedQty = this.rrd.qty;
    }
    this.isPageReady = true;
    await UtilFns.wait(1);
    this.qtyInput?.nativeElement?.focus();
    this.qtyInput?.nativeElement?.select();
  }

  get canSubmit(): boolean {
    return this.rrd != null
      && this.receivedQty != null
      && this.receivedQty >= 0
      && !this.submitting;
  }

  async confirm() {
    if (!this.canSubmit || !this.rrd) return;
    this.submitting = true;
    this.errorMessage = null;
    try {
      const actor = this.authService.getUser()?.initials ?? "SYS";
      const url = environment.apiRoot + "breeze/Uniformax/MarkItemsReceived";
      await firstValueFrom(this.http.post(url, {
        rrdId: this.rrd.id,
        receivedQty: this.receivedQty,
        actor: actor,
      }));
      this.toastr.success(`RRD #${this.rrd.id} received — moved to INSPECTING`);
      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Failed to record receipt.";
      this.errorMessage = msg;
    } finally {
      this.submitting = false;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
